import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ShoppingCart, Plus, Minus, Trash2, Share2, Copy, Tag, Truck, Award, Coins, X, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { startRazorpayCheckout } from '@/lib/razorpay';
import { startCashfreeCheckout, preloadCashfree } from '@/lib/cashfree';

const CART_KEY = 'indiva_store_cart';
const loadCart = () => { try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; } };
const saveCart = (c) => localStorage.setItem(CART_KEY, JSON.stringify(c));

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const Store = () => {
  const [params] = useSearchParams();
  const { productId } = useParams();
  const ref = params.get('ref') || null;
  const [data, setData] = useState({ products: [], categories: [], can_sell: false });
  const [cat, setCat] = useState('All');
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(loadCart());
  const [cartOpen, setCartOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [quote, setQuote] = useState(null);
  const [checkoutInfo, setCheckoutInfo] = useState({ full_name: '', phone: '', address: '' });
  const [paying, setPaying] = useState(false);
  const [myRef, setMyRef] = useState(ref);
  const [provider, setProvider] = useState('razorpay');
  const [reward, setReward] = useState(null);
  const [rewardMode, setRewardMode] = useState(false);
  const [sponsorReward, setSponsorReward] = useState(null);
  const [rewardKind, setRewardKind] = useState('weekly');
  const [voucherId, setVoucherId] = useState(null);

  useEffect(() => { preloadCashfree(); }, []);

  // Finish a Cashfree store payment after the hosted-checkout redirect returns.
  useEffect(() => {
    const cfOrder = params.get('cf_verify');
    if (!cfOrder) return;
    (async () => {
      try {
        const { data } = await api.post('/store/cashfree-verify', { order_id: cfOrder });
        if (data?.status === 'paid') {
          toast.success(data?.reward ? 'Reward claimed! 🎁' : 'Order placed! 🎉');
          setCart([]); saveCart([]); setCartOpen(false); setQuote(null); exitReward(); loadReward();
        } else {
          toast('Payment is still processing — we could not confirm it yet.');
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not confirm the Cashfree payment');
      } finally {
        const url = new URL(window.location.href);
        url.searchParams.delete('cf_verify');
        window.history.replaceState({}, '', url.pathname + url.search);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    (async () => {
      try { const res = await api.get('/store/my-sales'); if (res.data?.personal_id) setMyRef(prev => prev || res.data.personal_id); }
      catch { /* not a seller */ }
    })();
  }, []);

  const loadReward = useCallback(async () => {
    try { const { data } = await api.get('/network/weekly-reward'); setReward(data); }
    catch { setReward(null); /* guest / not a member */ }
    try { const { data } = await api.get('/network/sponsor-rewards'); setSponsorReward(data); }
    catch { setSponsorReward(null); }
  }, []);
  useEffect(() => { loadReward(); }, [loadReward]);

  const load = useCallback(async (c) => {
    setLoading(true);
    try {
      const res = await api.get(`/store/products${c && c !== 'All' ? `?category=${encodeURIComponent(c)}` : ''}`);
      setData(res.data);
    } catch { toast.error('Could not load store'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(cat); }, [cat, load]);
  useEffect(() => { saveCart(cart); }, [cart]);
  useEffect(() => {
    if (!productId) return;
    (async () => {
      try { const res = await api.get(`/store/products/${productId}${ref ? `?ref=${ref}` : ''}`); setDetail(res.data); }
      catch { toast.error('Product not found'); }
    })();
  }, [productId, ref]);

  const addToCart = (p, size = null, colour = null) => {
    setCart(prev => {
      const key = `${p.product_id}|${size || ''}|${colour || ''}`;
      const existing = prev.find(i => i.key === key);
      if (existing) return prev.map(i => i.key === key ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { key, product_id: p.product_id, title: p.title, price: p.offer_price, image: p.images?.[0], size, colour, qty: 1 }];
    });
    toast.success('Added to cart');
    setDetail(null);
  };
  const setQty = (key, d) => setCart(prev => prev.map(i => i.key === key ? { ...i, qty: Math.max(1, i.qty + d) } : i));
  const removeItem = (key) => setCart(prev => prev.filter(i => i.key !== key));
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const activeReward = rewardKind === 'sponsor' ? sponsorReward : reward;
  const rewardPv = rewardKind === 'sponsor' ? (sponsorReward?.bv_cap || 25) : (reward?.reward_pv || 25);
  const rewardBvOver = rewardMode && quote && (quote.total_bv || 0) > rewardPv + 1e-6;
  const rewardDelivery = rewardKind === 'sponsor' ? (sponsorReward?.delivery || 0) : (quote?.delivery || 0);
  const displayQuote = (rewardMode && activeReward && quote) ? {
    ...quote, subtotal: activeReward.price, gst_pct: activeReward.gst_pct, gst: activeReward.gst,
    delivery: rewardDelivery,
    total: Math.round((activeReward.price || 0) + (activeReward.gst || 0) + rewardDelivery),
  } : quote;
  const startReward = (kind = 'weekly', v = null) => {
    setRewardKind(kind); setVoucherId(v); setCart([]); setRewardMode(true); setCartOpen(true); setQuote(null);
    const cap = kind === 'sponsor' ? (sponsorReward?.bv_cap || 25) : (reward?.reward_pv || 25);
    toast('Reward mode — add up to ' + cap + ' BV of products');
  };
  const exitReward = () => { setRewardMode(false); setRewardKind('weekly'); setVoucherId(null); };

  const fetchQuote = useCallback(async () => {
    if (cart.length === 0) { setQuote(null); return; }
    try {
      const items = cart.map(i => ({ product_id: i.product_id, qty: i.qty, size: i.size, colour: i.colour }));
      const res = await api.post('/store/quote', { items, ref });
      setQuote(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Quote failed'); }
  }, [cart, ref]);
  useEffect(() => { if (cartOpen) fetchQuote(); }, [cartOpen, fetchQuote]);

  const MIN_ITEMS = 1;
  const checkout = async () => {
    if (cartCount < MIN_ITEMS) {
      toast.error(`Minimum ${MIN_ITEMS} items per order — add ${MIN_ITEMS - cartCount} more to continue`); return;
    }
    if (!checkoutInfo.full_name || !checkoutInfo.phone || !checkoutInfo.address) {
      toast.error('Please fill name, phone and delivery address'); return;
    }
    if (rewardBvOver) { toast.error(`Reward is limited to ${rewardPv} PV — remove some items`); return; }
    setPaying(true);
    try {
      const items = cart.map(i => ({ product_id: i.product_id, qty: i.qty, size: i.size, colour: i.colour }));
      const extra = rewardMode ? { reward: true, reward_type: rewardKind, sponsor_reward_id: rewardKind === 'sponsor' ? voucherId : undefined } : {};
      if (provider === 'cashfree') {
        await startCashfreeCheckout({
          createPath: '/store/checkout',
          orderPayload: { items, ref, ...checkoutInfo, ...extra },
          returnPath: '/store',
        });
        return; // browser redirects to Cashfree hosted checkout
      }
      await startRazorpayCheckout({
        createPath: '/store/checkout',
        verifyPath: '/store/verify',
        orderPayload: { items, ref, ...checkoutInfo, ...extra },
        phone: checkoutInfo.phone,
        onSuccess: () => {
          toast.success(rewardMode ? 'Reward claimed! 🎁' : 'Order placed! 🎉');
          setCart([]); setCartOpen(false); setQuote(null); exitReward(); loadReward();
        },
      });
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || 'Payment failed'); }
    finally { setPaying(false); }
  };

  const sharePaymentLink = async () => {
    if (cart.length === 0) { toast.error('Add products to the cart first'); return; }
    try {
      const items = cart.map(i => ({ product_id: i.product_id, qty: i.qty, size: i.size, colour: i.colour }));
      const { data } = await api.post('/payment-links', { kind: 'store', items });
      const url = `${window.location.origin}${data.path}`;
      const text = `Complete your INDIVALIFE order & pay securely 👇\n${url}`;
      try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
      if (navigator.share) {
        try { await navigator.share({ title: 'INDIVALIFE payment link', text, url }); return; } catch { /* fall through */ }
      }
      toast.success('Payment link copied — share it on WhatsApp!');
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create payment link (sellers only)');
    }
  };

  const shareProduct = async (p, useMyRef = false) => {
    const r = useMyRef ? myRef : (ref || myRef);
    const appUrl = `${window.location.origin}/store/p/${p.product_id}${r ? `?ref=${r}` : ''}`;
    const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/store/share/${p.product_id}${r ? `?ref=${r}` : ''}`;
    const text = `${p.title} — ${money(p.offer_price)} on INDIVALIFE Store 🛍️\n${previewUrl}`;
    // Try native share WITH the product image file (mobile)
    const imgUrl = p.images?.[0];
    if (navigator.share && imgUrl) {
      try {
        const resp = await fetch(imgUrl);
        const blob = await resp.blob();
        const file = new File([blob], `${p.product_id}.jpg`, { type: blob.type || 'image/jpeg' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ title: p.title, text: `${p.title} — ${money(p.offer_price)} on INDIVALIFE\n${previewUrl}`, files: [file] });
          return;
        }
      } catch { /* fall through */ }
    }
    if (navigator.share) {
      try { await navigator.share({ title: p.title, text, url: previewUrl }); return; } catch { /* fall through */ }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const buildCaption = (p, previewUrl) => {
    const disc = p.mrp > p.offer_price ? Math.round((1 - p.offer_price / p.mrp) * 100) : 0;
    const lines = [
      `🛍️ ${p.title}`,
      disc > 0 ? `💰 Now ${money(p.offer_price)}  (M.R.P ${money(p.mrp)} · ${disc}% OFF)` : `💰 ${money(p.offer_price)}`,
      `✅ Genuine product · secure payment · home delivery`,
      `🛒 Order here 👇`,
      previewUrl,
      `#INDIVALIFE #ShopNow`,
    ];
    return lines.filter(Boolean).join('\n');
  };

  const sellThis = async (p) => {
    const r = myRef;
    const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/store/share/${p.product_id}${r ? `?ref=${r}` : ''}`;
    const caption = buildCaption(p, previewUrl);
    try { await navigator.clipboard.writeText(caption); } catch { /* ignore */ }
    const imgUrl = p.images?.[0];
    // Try native share with the image file + caption so they can post to WhatsApp Status / Insta in one tap
    if (navigator.share && imgUrl) {
      try {
        const resp = await fetch(imgUrl);
        const blob = await resp.blob();
        const file = new File([blob], `${p.product_id}.jpg`, { type: blob.type || 'image/jpeg' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          toast.success('Caption copied! Pick WhatsApp Status or Instagram to post.');
          await navigator.share({ title: p.title, text: caption, files: [file] });
          return;
        }
      } catch { /* fall through */ }
    }
    if (navigator.share) {
      try { toast.success('Caption copied — paste it in your status'); await navigator.share({ title: p.title, text: caption, url: previewUrl }); return; } catch { /* fall through */ }
    }
    toast.success('Caption + link copied! Paste it in your WhatsApp Status or Instagram.');
    window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, '_blank');
  };

  return (
    <main className="max-w-screen-2xl mx-auto p-4 sm:p-6 bg-slate-50 min-h-screen" data-testid="store-page">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black display text-[#1E3A8A] flex items-center gap-2">
            <span className="inline-grid place-items-center h-9 w-9 rounded-2xl bg-[#1D4ED8] text-white"><ShoppingCart className="h-5 w-5" /></span>
            INDIVALIFE Store
          </h1>
          <p className="text-sm text-[var(--bz-muted)] mt-0.5">Genuine products · secure payment · fast delivery{ref ? ` · shared by ${ref}` : ''}</p>
        </div>
        <Button onClick={() => setCartOpen(true)} className="relative rounded-full bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-2 active:scale-95 transition-transform" data-testid="open-cart-btn">
          <ShoppingCart className="h-4 w-4" /> Cart
          {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-[var(--bz-orange)] text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center" data-testid="cart-count">{cartCount}</span>}
        </Button>
      </div>

      {/* Promo banner */}
      <div className="rounded-3xl bg-gradient-to-r from-[var(--bz-orange)] to-[#ff8a3d] text-white p-4 sm:p-5 shadow-lg relative overflow-hidden mb-5 flex items-center justify-between gap-3">
        <div className="absolute -bottom-8 -right-6 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold"><Coins className="h-3.5 w-3.5" /> Rewards on every order</div>
          <div className="text-lg sm:text-2xl font-black display mt-0.5 leading-tight">Earn BV + Cashback when you shop &amp; sell</div>
        </div>
        <Award className="relative h-10 w-10 sm:h-14 sm:w-14 opacity-90 shrink-0" />
      </div>

      {((reward?.eligible) || (sponsorReward?.enabled && sponsorReward?.available_count > 0)) && (
        <div className="rounded-2xl border-2 border-[var(--bz-orange)] bg-[var(--bz-orange-tint)] p-4 mb-5 flex items-center justify-between gap-3" data-testid="reward-redirect-banner">
          <div>
            <div className="text-sm font-black text-[#1E3A8A] flex items-center gap-1.5"><Award className="h-4 w-4 text-[var(--bz-orange)]" />You have free product rewards!</div>
            <div className="text-xs text-[var(--bz-muted)] mt-0.5">Your rewards are shown on your Business page and can be claimed there after the hold period.</div>
          </div>
          <Button onClick={() => window.location.assign('/business')} className="rounded-full bg-[var(--bz-orange)] hover:bg-[#e65100] text-white shrink-0" data-testid="go-to-business-rewards-btn">View rewards</Button>
        </div>
      )}
      {reward && !reward.eligible && reward.enabled && (reward.current_week_pv > 0) && (
        <div className="rounded-2xl border bg-white p-3 mb-5 text-xs" data-testid="reward-progress-banner">
          <div className="flex items-center justify-between font-semibold text-[#1E3A8A]">
            <span>This week's PV → weekly reward</span>
            <span>{reward.current_week_pv}/{reward.threshold_pv} PV</span>
          </div>
          <div className="h-1.5 rounded-full bg-[var(--bz-line)] overflow-hidden mt-1">
            <div className="h-full bg-[var(--bz-orange)]" style={{ width: `${Math.min(100, (reward.current_week_pv / reward.threshold_pv) * 100)}%` }} />
          </div>
          <div className="text-[10px] text-[var(--bz-muted)] mt-0.5">Buy {reward.threshold_pv} PV this business week to earn a {reward.reward_pv} PV free-product reward (claimable on your Business page after the hold period).</div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap mb-5" data-testid="store-categories">
        {['All', ...data.categories].map(c => (
          <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-all active:scale-95 ${cat === c ? 'bg-[#1D4ED8] text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200 hover:border-[#1D4ED8]/50'}`}>
            {c}
          </button>
        ))}
      </div>

      {loading && <div className="text-center text-[var(--bz-muted)] py-16">Loading store…</div>}
      {!loading && data.products.length === 0 && (
        <Card className="p-10 text-center text-[var(--bz-muted)]" data-testid="store-empty">No products yet. Check back soon!</Card>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {data.products.map(p => {
          const disc = p.mrp > p.offer_price ? Math.round((1 - p.offer_price / p.mrp) * 100) : 0;
          return (
            <Card key={p.product_id} className="rounded-[22px] border border-slate-100 shadow-sm overflow-hidden flex flex-col group hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid={`product-card-${p.product_id}`}>
              <button onClick={() => setDetail(p)} className="relative aspect-square bg-slate-50 overflow-hidden">
                <img src={p.images?.[0] || 'https://placehold.co/400?text=INDIVALIFE'} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {disc > 0 && <span className="absolute top-2.5 left-2.5 bg-[var(--bz-orange)] text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow">{disc}% OFF</span>}
                {data.can_sell && p.bv > 0 && <span className="absolute top-2.5 right-2.5 bg-[var(--bz-success)] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow flex items-center gap-0.5"><Award className="h-3 w-3" />{p.bv} BV</span>}
                {!p.in_stock && <span className="absolute inset-0 bg-black/50 text-white flex items-center justify-center text-sm font-bold">Out of stock</span>}
              </button>
              <div className="p-3 flex flex-col flex-1">
                <div className="text-sm font-semibold line-clamp-2 min-h-[2.5rem] leading-snug">{p.title}</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-lg font-black display text-[var(--bz-orange)]">{money(p.offer_price)}</span>
                  {disc > 0 && <span className="text-xs text-slate-400 line-through">{money(p.mrp)}</span>}
                </div>
                {data.can_sell && p.cashback > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap" data-testid={`rewards-${p.product_id}`}>
                    <span className="text-[10px] bg-[var(--bz-success-tint)] text-[#00863a] rounded-full px-2 py-0.5 font-bold flex items-center gap-0.5"><Coins className="h-3 w-3" />{money(p.cashback)} cashback</span>
                  </div>
                )}
                <div className="mt-1 text-[11px] font-semibold" data-testid={`stock-${p.product_id}`}>
                  {!p.in_stock ? (
                    <span className="text-red-600">Out of stock</span>
                  ) : p.stock_count <= 10 ? (
                    <span className="text-amber-600">Only {p.stock_count} left in stock</span>
                  ) : (
                    <span className="text-[#00863a]">In stock: {p.stock_count}</span>
                  )}
                </div>
                <div className="mt-auto pt-2 flex gap-1.5">
                  <Button size="sm" disabled={!p.in_stock} onClick={() => (p.sizes?.length || p.colours?.length) ? setDetail(p) : addToCart(p)} className="flex-1 rounded-full bg-[var(--bz-orange)] hover:bg-[#e65100] text-xs font-bold active:scale-95 transition-transform" data-testid={`add-cart-${p.product_id}`}>
                    <Plus className="h-3.5 w-3.5 mr-1" />{(p.sizes?.length || p.colours?.length) ? 'Select' : 'Add'}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => shareProduct(p)} data-testid={`share-${p.product_id}`}><Share2 className="h-3.5 w-3.5" /></Button>
                </div>
                {data.can_sell && (
                  <Button size="sm" variant="outline" onClick={() => sellThis(p)} className="w-full mt-1.5 rounded-full border-[#1D4ED8] text-[#1E3A8A] text-xs gap-1 font-semibold" data-testid={`sell-this-${p.product_id}`}>
                    <Tag className="h-3.5 w-3.5" />Sell this &amp; earn
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Product detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-detail">
          {detail && <ProductDetail p={detail} canSell={data.can_sell} onAdd={addToCart} onShare={() => shareProduct(detail)} onSell={() => sellThis(detail)} />}
        </DialogContent>
      </Dialog>

      {/* Cart */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="cart-dialog">
          <DialogHeader><DialogTitle>{rewardMode ? (rewardKind === 'sponsor' ? '🎁 Claim your sponsor reward' : '🎁 Claim your PV reward') : 'Your cart'}</DialogTitle></DialogHeader>
          {rewardMode && activeReward && (
            <div className={`rounded-lg p-2.5 text-xs font-semibold mb-1 ${rewardBvOver ? 'bg-red-50 text-red-600' : 'bg-[var(--bz-green-tint)] text-[#1E3A8A]'}`} data-testid="reward-cart-banner">
              Add up to {rewardPv} BV of products · used {quote?.total_bv || 0}/{rewardPv} BV · you pay a flat ₹{activeReward.price} + {activeReward.gst_pct}% GST + delivery.
              {rewardBvOver && <span className="block">Over the {rewardPv} BV limit — remove some items.</span>}
            </div>
          )}
          {cart.length === 0 ? (
            <div className="text-center text-[var(--bz-muted)] py-10" data-testid="cart-empty">Cart is empty.</div>
          ) : (
            <div className="space-y-3">
              {cart.map(i => (
                <div key={i.key} className="flex items-center gap-3 border-b pb-2" data-testid={`cart-line-${i.product_id}`}>
                  <img src={i.image || 'https://placehold.co/80'} alt="" className="h-14 w-14 rounded object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{i.title}</div>
                    <div className="text-[11px] text-[var(--bz-muted)]">{[i.size, i.colour].filter(Boolean).join(' · ')} {money(i.price)}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => setQty(i.key, -1)}><Minus className="h-3 w-3" /></Button>
                      <span className="text-sm font-bold w-5 text-center" data-testid={`qty-${i.product_id}`}>{i.qty}</span>
                      <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => setQty(i.key, 1)}><Plus className="h-3 w-3" /></Button>
                    </div>
                  </div>
                  <button onClick={() => removeItem(i.key)} className="text-red-500" data-testid={`remove-${i.product_id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {displayQuote && (
                <div className="bg-[var(--bz-soft)] rounded-md p-3 text-sm space-y-1" data-testid="cart-quote">
                  <div className="flex justify-between"><span>{rewardMode ? 'Reward price' : 'Subtotal'}</span><span>{money(displayQuote.subtotal)}</span></div>
                  <div className="flex justify-between"><span>GST ({displayQuote.gst_pct}%)</span><span>{money(displayQuote.gst)}</span></div>
                  <div className="flex justify-between"><span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5" />Delivery</span><span>{money(displayQuote.delivery)}</span></div>
                  {!rewardMode && <div className="flex justify-between text-[11px] text-[var(--bz-muted)]"><span>Total PV</span><span>{quote.total_bv || 0} PV</span></div>}
                  <div className="flex justify-between font-black text-base pt-1 border-t"><span>Total</span><span className="text-[#1E3A8A]">{money(displayQuote.total)}</span></div>
                </div>
              )}
              <div className="space-y-2">
                <Input placeholder="Full name" value={checkoutInfo.full_name} onChange={e => setCheckoutInfo({ ...checkoutInfo, full_name: e.target.value })} data-testid="checkout-name" />
                <Input placeholder="Phone (10-digit)" value={checkoutInfo.phone} onChange={e => setCheckoutInfo({ ...checkoutInfo, phone: e.target.value })} data-testid="checkout-phone" />
                <Input placeholder="Delivery address" value={checkoutInfo.address} onChange={e => setCheckoutInfo({ ...checkoutInfo, address: e.target.value })} data-testid="checkout-address" />
              </div>
              {cartCount < MIN_ITEMS && (
                <div className="text-xs font-semibold text-[var(--bz-orange)] bg-[var(--bz-orange-tint)] rounded-lg px-3 py-2" data-testid="min-items-notice">
                  Minimum {MIN_ITEMS} items per order — add {MIN_ITEMS - cartCount} more to check out.
                </div>
              )}
              <div>
                <div className="text-[11px] font-semibold text-slate-500 mb-1.5">Pay with</div>
                <div className="grid grid-cols-2 gap-2" data-testid="store-provider-selector">
                  {[['razorpay', 'Razorpay'], ['cashfree', 'Cashfree']].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => setProvider(val)} data-testid={`store-provider-${val}`}
                      className={`rounded-xl border-2 py-2 text-sm font-bold transition-all ${provider === val ? 'border-[#1D4ED8] bg-[var(--bz-soft)] text-[#1E3A8A]' : 'border-slate-200 text-slate-500'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={checkout} disabled={paying || cartCount < MIN_ITEMS || rewardBvOver} className="w-full rounded-full bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid="checkout-btn">
                {paying ? 'Processing…' : `Pay ${displayQuote ? money(displayQuote.total) : ''} with ${provider === 'cashfree' ? 'Cashfree' : 'Razorpay'}`}
              </Button>
              {rewardMode && (
                <button onClick={() => { exitReward(); setCart([]); setQuote(null); }} className="w-full text-xs text-[var(--bz-muted)] underline" data-testid="exit-reward-mode">Exit reward mode</button>
              )}
              {data.can_sell && (
                <Button onClick={sharePaymentLink} variant="outline" className="w-full rounded-full border-[#1D4ED8] text-[#1E3A8A] gap-1.5 font-semibold" data-testid="share-payment-link-btn">
                  <Link2 className="h-4 w-4" />Share this cart as a payment link
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

const ProductDetail = ({ p, canSell, onAdd, onShare, onSell }) => {
  const [img, setImg] = useState(0);
  const [size, setSize] = useState(p.sizes?.[0] || null);
  const [colour, setColour] = useState(p.colours?.[0] || null);
  const disc = p.mrp > p.offer_price ? Math.round((1 - p.offer_price / p.mrp) * 100) : 0;
  const images = p.images?.length ? p.images : ['https://placehold.co/600?text=INDIVALIFE'];
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <div className="aspect-square rounded-lg overflow-hidden bg-[var(--bz-soft)]">
          <img src={images[img]} alt={p.title} className="w-full h-full object-cover" />
        </div>
        {images.length > 1 && (
          <div className="flex gap-2 mt-2 overflow-x-auto">
            {images.map((im, idx) => (
              <button key={idx} onClick={() => setImg(idx)} className={`h-14 w-14 rounded border-2 overflow-hidden shrink-0 ${img === idx ? 'border-[#1D4ED8]' : 'border-transparent'}`}>
                <img src={im} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
      <div>
        <DialogTitle className="text-lg display">{p.title}</DialogTitle>
        <div className="flex items-baseline gap-2 mt-2">
          <span className="text-2xl font-black display text-[var(--bz-orange)]">{money(p.offer_price)}</span>
          {disc > 0 && <><span className="text-sm text-slate-400 line-through">{money(p.mrp)}</span><span className="text-sm text-[var(--bz-orange)] font-bold">{disc}% off</span></>}
        </div>
        {canSell && (p.bv > 0 || p.cashback > 0) && (
          <div className="flex gap-2 mt-2">
            {p.bv > 0 && <Badge className="bg-[var(--bz-success-tint)] text-[#00863a] gap-1"><Award className="h-3 w-3" />{p.bv} BV</Badge>}
            {p.cashback > 0 && <Badge className="bg-[var(--bz-success-tint)] text-[#00863a] gap-1"><Coins className="h-3 w-3" />{money(p.cashback)} cashback</Badge>}
          </div>
        )}
        {p.hsn_code && <div className="text-[11px] text-[var(--bz-muted)] mt-1">HSN: {p.hsn_code}</div>}
        <div className="mt-2 text-sm font-semibold" data-testid="detail-stock">
          {!p.in_stock ? (
            <span className="text-red-600">Out of stock</span>
          ) : p.stock_count <= 10 ? (
            <span className="text-amber-600">Only {p.stock_count} left in stock</span>
          ) : (
            <span className="text-green-600">In stock: {p.stock_count} available</span>
          )}
        </div>
        <p className="text-sm text-[var(--bz-muted)] mt-3 whitespace-pre-line">{p.description}</p>
        {p.sizes?.length > 0 && (
          <div className="mt-3"><div className="text-xs font-semibold mb-1">Size</div>
            <div className="flex gap-2 flex-wrap">{p.sizes.map(s => <button key={s} onClick={() => setSize(s)} className={`px-3 py-1 rounded border text-sm ${size === s ? 'border-[#1D4ED8] bg-[var(--bz-green-tint)]' : ''}`} data-testid={`size-${s}`}>{s}</button>)}</div>
          </div>
        )}
        {p.colours?.length > 0 && (
          <div className="mt-3"><div className="text-xs font-semibold mb-1">Colour</div>
            <div className="flex gap-2 flex-wrap">{p.colours.map(c => <button key={c} onClick={() => setColour(c)} className={`px-3 py-1 rounded border text-sm ${colour === c ? 'border-[#1D4ED8] bg-[var(--bz-green-tint)]' : ''}`} data-testid={`colour-${c}`}>{c}</button>)}</div>
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <Button disabled={!p.in_stock} onClick={() => onAdd(p, size, colour)} className="flex-1 rounded-full bg-[var(--bz-orange)] hover:bg-[#e65100] font-bold active:scale-95 transition-transform" data-testid="detail-add-cart">
            <Plus className="h-4 w-4 mr-1" />{p.in_stock ? 'Add to cart' : 'Out of stock'}
          </Button>
          <Button variant="outline" className="rounded-full" onClick={onShare} data-testid="detail-share"><Share2 className="h-4 w-4" /></Button>
        </div>
        {canSell && (
          <Button variant="outline" onClick={onSell} className="w-full mt-2 rounded-full border-[#1D4ED8] text-[#1E3A8A] gap-1 font-semibold" data-testid="detail-sell-this">
            <Tag className="h-4 w-4" />Sell this &amp; earn — copy caption + image
          </Button>
        )}
      </div>
    </div>
  );
};

export default Store;
